// AutomationLiteralInput.jsx

import React from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Utilities
import { CHECKLIST_ITEM_CONDITION_OPERATORS } from '../utils/automationConditionFilters';
import { peopleLiteralToText, tagsLiteralToText } from '../utils/automationValueUtils';

// CSS
import './AutomationLiteralInput.css';

// Components
import CheckboxList from 'components/checkboxlist/CheckboxList';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * AutomationLiteralInput
 *
 * Renders the correct literal value input control for a selected field type.
 *
 * @param {string} fieldKey - Selected field key.
 * @param {string} operator - Selected operator used for enum multi-select behavior.
 * @param {*} value - Current literal value.
 * @param {function} onValueChange - Callback for value updates.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {Map} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {Map} fieldEnumOptionsByKey - Enum option lookup keyed by field key.
 * @param {array} [baseMembers=[]] - Base members for people-literal labels.
 *
 * @returns {ReactNode} The rendered literal input control.
 */
const AutomationLiteralInput = React.memo(({
  fieldKey,
  operator,
  value,
  onValueChange,
  theme,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  baseMembers = [],
}) => {

  const fieldType = fieldTypeByKey.get(fieldKey) || 'string';

  if (fieldType === 'checklist' && CHECKLIST_ITEM_CONDITION_OPERATORS.has(operator)) {
    const options = fieldEnumOptionsByKey.get(fieldKey) || [];
    const normalizedValue = String(Array.isArray(value) ? (value[0] || '') : (value ?? '')).trim();
    const safeValue = options.some((option) => option.value === normalizedValue)
      ? normalizedValue
      : '';

    return (
      <SelectInput
        style={{
          fontSize: '12px',
        }}
        value={safeValue}
        options={options.map((option) => ({ value: option.value, label: option.label }))}
        placeholder="Select item..."
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    const options = fieldEnumOptionsByKey.get(fieldKey) || [];
    const isMultiOperator = operator === 'is_any_of' || operator === 'is_none_of';

    if (isMultiOperator) {
      const rawValues = Array.isArray(value)
        ? value
        : String(value ?? '').split(',').map((entry) => String(entry || '').trim()).filter(Boolean);
      const safeValues = rawValues.filter((entry) => options.some((option) => option.value === entry));

      return (
        <CheckboxList
          className="automation-modal-checkbox-list"
          options={options.map((option) => ({ value: option.value, label: option.label }))}
          values={safeValues}
          checkboxSize={18}
          multiSelect
          onChange={(nextValues) => onValueChange(nextValues)}
        />
      );
    }

    const normalizedValue = String(Array.isArray(value) ? (value[0] || '') : (value ?? '')).trim();
    const safeValue = options.some((option) => option.value === normalizedValue)
      ? normalizedValue
      : '';

    return (
      <SelectInput
        style={{
          fontSize: '12px',
        }}
        value={safeValue}
        options={options.map((option) => ({ value: option.value, label: option.label }))}
        placeholder="Select option..."
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'boolean') {
    return (
      <SelectInput
        style={{
          fontSize: '12px',
        }}
        value={String(value === true)}
        options={[
          { value: 'true', label: 'true' },
          { value: 'false', label: 'false' },
        ]}
        onChange={(event) => onValueChange(event.target.value === 'true')}
      />
    );
  }

  if (fieldType === 'number' || fieldType === 'money' || fieldType === 'percentage') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="number"
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'date' || fieldType === 'daterange') {
    const normalizedValue = typeof value === 'string' ? value : '';
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="date"
        value={normalizedValue}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'datetime' || fieldType === 'datetimerange') {
    const normalizedValue = typeof value === 'string' ? value : '';
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="datetime-local"
        value={normalizedValue}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'email') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="email"
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'url') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="url"
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'phone') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        type="tel"
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'tags') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        placeholder="tag1, tag2"
        type="text"
        value={tagsLiteralToText(value)}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'people') {
    return (
      <input
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        placeholder="Pick people"
        type="text"
        value={peopleLiteralToText(value, baseMembers)}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  if (fieldType === 'longtext' || fieldType === 'textarea' || fieldType === 'text') {

    return (
      <textarea
        className="automation-rule-input"
        style={{
          backgroundColor: theme.panel,
          borderColor: theme.backgroundMuted,
          color: theme.foreground,
        }}
        rows={3}
        value={value ?? ''}
        onChange={(event) => onValueChange(event.target.value)}
      />
    );
  }

  // ----- RENDER ----- //

  return (
    <input
      className="automation-rule-input"
      style={{
        backgroundColor: theme.panel,
        borderColor: theme.backgroundMuted,
        color: theme.foreground,
      }}
      type="text"
      value={value ?? ''}
      onChange={(event) => onValueChange(event.target.value)}
    />
  );
});

// Dev Tools
AutomationLiteralInput.displayName = 'AutomationLiteralInput';

export default AutomationLiteralInput;
